import React, { useState, useEffect } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Skeleton, Chip, TablePagination, Button, Typography, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { HousekeepingService } from '../../api/services/housekeepingService';
import { toast } from '../../utils/toast';
import ExportFormatModal from '../../components/ExportFormatModal';
import { exportClientSide } from '../../utils/exportUtils';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { extractTotalCount, standardFilterContainerSx, standardSearchInputSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../components/common/tableStyles';

export default function PerformanceAnalyticsTab({ tenantId }) {
    const [performanceData, setPerformanceData] = useState([]);
    const [isLoadingPerformance, setIsLoadingPerformance] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [total, setTotal] = useState(0);
    const [perfFilters, setPerfFilters] = useState({ staff: '', dateRange: '' });
    const [exportModalOpen, setExportModalOpen] = useState(false);

    const fetchPerformanceReport = async () => {
        setIsLoadingPerformance(true);
        try {
            const params = {
                page,
                limit: rowsPerPage,
                staff: perfFilters.staff || undefined,
            };
            const data = await HousekeepingService.getPerformanceReport(tenantId, params);
            const reports = data?.data?.reports || data?.reports || data?.data || data || [];
            setPerformanceData(Array.isArray(reports) ? reports : []);
            setTotal(extractTotalCount(data, reports));
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to load performance report");
        } finally {
            setIsLoadingPerformance(false);
        }
    };

    useEffect(() => {
        fetchPerformanceReport();
    }, [tenantId, page, rowsPerPage, perfFilters]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handlePerfFilterChange = (e) => {
        setPerfFilters({ ...perfFilters, [e.target.name]: e.target.value });
        setPage(0);
    };

    const handleExport = (format) => {
        setExportModalOpen(false);
        try {
            exportClientSide(format, filteredPerformanceData, 'performance_report');
            toast.success('Export successful');
        } catch (error) {
            toast.error(error.message || 'Export failed');
        }
    };

    const paginatedPerformanceData = performanceData;

    return (
        <Box>
            <Box sx={standardFilterContainerSx}>
                <Box>
                    <Typography variant="h6" fontWeight="bold">Housekeeper Performance Report</Typography>
                    <Typography variant="body2" color="text.secondary">Snapshot of operational efficiency and completed tasks metrics.</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField 
                        size="small" 
                        name="staff" 
                        value={perfFilters.staff} 
                        onChange={handlePerfFilterChange} 
                        placeholder="Search by name..."
                        sx={standardSearchInputSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#98a0aa', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    {(perfFilters.staff || perfFilters.dateRange) && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={() => setPerfFilters({ staff: '', dateRange: '' })}>Reset</Button>
                    )}
                    <Button variant="outlined" startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />} sx={standardSecondaryButtonSx} onClick={() => setExportModalOpen(true)}>
                        Export Options
                    </Button>
                    <ExportFormatModal
                        open={exportModalOpen}
                        onClose={() => setExportModalOpen(false)}
                        onExport={handleExport}
                        isExporting={false}
                        title="Export Performance Report"
                    />
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Housekeeper Name</TableCell>
                            <TableCell align="center">Total Tasks Completed</TableCell>
                            <TableCell align="center">Avg. Duration (mins)</TableCell>
                            <TableCell align="center">Efficiency Score</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoadingPerformance ? (
                            [1, 2, 3].map((row) => (
                                <TableRow key={row}>
                                    <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                                    <TableCell><Skeleton variant="text" width="40%" sx={{ mx: 'auto' }} /></TableCell>
                                    <TableCell><Skeleton variant="text" width="40%" sx={{ mx: 'auto' }} /></TableCell>
                                    <TableCell><Skeleton variant="circular" width={30} height={30} sx={{ mx: 'auto' }} /></TableCell>
                                </TableRow>
                            ))
                        ) : performanceData.length === 0 ? (
                            <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No performance metrics available.</TableCell></TableRow>
                        ) : (
                            paginatedPerformanceData.map((item, index) => {
                                const score = item.efficiencyScore || item.score || item.rating || 0;
                                return (
                                    <TableRow key={item.id || index} hover>
                                        <TableCell><b>{item.staffName || item.name || item.housekeeperName || item.employeeName || 'Unknown Staff'}</b></TableCell>
                                        <TableCell align="center">
                                            <Chip label={item.totalTasks || item.tasksCompleted || item.completedTasks || 0} size="small" color="primary" />
                                        </TableCell>
                                        <TableCell align="center">{item.avgDuration || item.averageDuration || item.avgCleaningTime || 0}m</TableCell>
                                        <TableCell align="center">
                                            <Chip 
                                                label={score >= 90 ? 'A+' : score >= 80 ? 'A' : score >= 70 ? 'B' : 'C'} 
                                                size="small" 
                                                color={score >= 85 ? 'success' : score >= 70 ? 'warning' : 'error'} 
                                                variant="outlined"
                                                sx={{ fontWeight: 'bold' }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={performanceData.length === 0 ? 0 : total}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[20, 50, 100]}
                />
            </TableContainer>
        </Box>
    );
}
